import { useState } from 'react';
import PropTypes from 'prop-types';
import toast from 'react-hot-toast';
import {
  AcademicCapIcon,
  ChatBubbleLeftRightIcon,
  CodeBracketIcon,
  LightBulbIcon,
  QuestionMarkCircleIcon,
  ClipboardDocumentIcon,
  CheckIcon,
  ChevronDownIcon,
  ChevronUpIcon,
  SparklesIcon
} from '@heroicons/react/24/outline';
import { Button, Loading } from './common';
import { generateInterviewPrep } from '../services/generateService';

/**
 * InterviewPrepCard - Generates and displays interview preparation materials
 */
const InterviewPrepCard = ({ jobId, resumeId, jobTitle, company }) => {
  const [prepData, setPrepData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [expandedSections, setExpandedSections] = useState({
    role: true,
    company: false,
    technical: false,
    talking: true,
    questions: false,
    tips: false
  });
  const [copiedIndex, setCopiedIndex] = useState(null);

  const handleGenerate = async () => {
    setLoading(true);
    try {
      const response = await generateInterviewPrep(jobId, resumeId);
      if (response.success) {
        setPrepData(response.data);
        toast.success('Interview prep generated!');
      }
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to generate interview prep');
    } finally {
      setLoading(false);
    }
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => ({
      ...prev,
      [section]: !prev[section]
    }));
  };

  const copyToClipboard = (text, index) => {
    navigator.clipboard.writeText(text);
    setCopiedIndex(index);
    toast.success('Copied!');
    setTimeout(() => setCopiedIndex(null), 2000);
  };

  // Not generated yet - show CTA
  if (!prepData) {
    return (
      <div className="bg-gradient-to-r from-indigo-50 to-purple-50 border border-indigo-200 rounded-xl p-6">
        <div className="flex items-start justify-between">
          <div className="flex items-start gap-4">
            <div className="p-3 bg-indigo-100 rounded-lg">
              <AcademicCapIcon className="h-6 w-6 text-indigo-600" />
            </div>
            <div>
              <h3 className="font-semibold text-gray-900">Interview Prep</h3>
              <p className="text-sm text-gray-600 mt-1">
                Get likely interview questions, talking points, and preparation tips
                {jobTitle && <span> for <strong>{jobTitle}</strong></span>}
                {company && <span> at <strong>{company}</strong></span>}
              </p>
            </div>
          </div>
          <Button onClick={handleGenerate} loading={loading}>
            <SparklesIcon className="h-5 w-5 mr-2" />
            {loading ? 'Generating...' : 'Prep for Interview'}
          </Button>
        </div>
      </div>
    );
  }

  // Section component
  const Section = ({ id, icon: Icon, title, count, children }) => (
    <div className="border-b border-gray-100 last:border-0">
      <button
        onClick={() => toggleSection(id)}
        className="w-full flex items-center justify-between p-4 hover:bg-gray-50 transition-colors"
      >
        <div className="flex items-center gap-3">
          <div className="w-8 h-8 bg-indigo-100 rounded-lg flex items-center justify-center">
            <Icon className="h-4 w-4 text-indigo-600" />
          </div>
          <span className="font-medium text-gray-900">{title}</span>
          {count > 0 && (
            <span className="text-xs text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full">
              {count}
            </span>
          )}
        </div>
        {expandedSections[id] ? (
          <ChevronUpIcon className="h-5 w-5 text-gray-400" />
        ) : (
          <ChevronDownIcon className="h-5 w-5 text-gray-400" />
        )}
      </button>
      {expandedSections[id] && (
        <div className="px-4 pb-4">
          {children}
        </div>
      )}
    </div>
  );

  Section.propTypes = {
    id: PropTypes.string.isRequired,
    icon: PropTypes.elementType.isRequired,
    title: PropTypes.string.isRequired,
    count: PropTypes.number,
    children: PropTypes.node.isRequired
  };

  return (
    <div className="bg-white border border-gray-200 rounded-xl overflow-hidden shadow-sm">
      {/* Header */}
      <div className="bg-gradient-to-r from-indigo-500 to-purple-500 p-6 text-white">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <AcademicCapIcon className="h-8 w-8" />
            <div>
              <h3 className="font-semibold text-lg">Interview Prep Ready</h3>
              <p className="text-sm text-indigo-100">
                Prepared for {prepData.jobTitle} at {prepData.company}
              </p>
            </div>
          </div>
          <Button
            variant="ghost"
            size="sm"
            onClick={handleGenerate}
            loading={loading}
            className="text-white hover:bg-white/20"
          >
            <SparklesIcon className="h-4 w-4 mr-1" />
            Regenerate
          </Button>
        </div>
      </div>

      {/* Sections */}
      <div className="divide-y divide-gray-100">
        {/* Role Questions */}
        <Section
          id="role"
          icon={ChatBubbleLeftRightIcon}
          title="Likely Interview Questions"
          count={prepData.roleQuestions?.length}
        >
          <div className="space-y-4">
            {prepData.roleQuestions?.map((q, i) => (
              <div key={i} className="bg-gray-50 rounded-lg p-4">
                <div className="flex items-start justify-between gap-2">
                  <p className="font-medium text-gray-900">{q.question}</p>
                  <button
                    onClick={() => copyToClipboard(q.question, `role-${i}`)}
                    className="text-gray-400 hover:text-gray-600 flex-shrink-0"
                  >
                    {copiedIndex === `role-${i}` ? (
                      <CheckIcon className="h-4 w-4 text-green-500" />
                    ) : (
                      <ClipboardDocumentIcon className="h-4 w-4" />
                    )}
                  </button>
                </div>
                {q.tip && (
                  <p className="text-sm text-indigo-600 mt-2 flex items-start gap-1">
                    <LightBulbIcon className="h-4 w-4 mt-0.5 flex-shrink-0" />
                    {q.tip}
                  </p>
                )}
                {q.sampleAnswer && (
                  <p className="text-sm text-gray-600 mt-2 bg-white p-3 rounded border border-gray-200">
                    <span className="font-medium text-gray-700">Sample approach: </span>
                    {q.sampleAnswer}
                  </p>
                )}
              </div>
            ))}
          </div>
        </Section>

        {/* Technical Questions */}
        {prepData.technicalQuestions?.length > 0 && (
          <Section
            id="technical"
            icon={CodeBracketIcon}
            title="Technical Questions"
            count={prepData.technicalQuestions?.length}
          >
            <div className="space-y-3">
              {prepData.technicalQuestions?.map((q, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900">{q.question}</p>
                  {q.tip && (
                    <p className="text-sm text-gray-600 mt-2 flex items-start gap-1">
                      <LightBulbIcon className="h-4 w-4 mt-0.5 flex-shrink-0 text-amber-500" />
                      {q.tip}
                    </p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Company Questions */}
        {prepData.companyQuestions?.length > 0 && (
          <Section
            id="company"
            icon={ChatBubbleLeftRightIcon}
            title="Company & Culture Questions"
            count={prepData.companyQuestions?.length}
          >
            <div className="space-y-3">
              {prepData.companyQuestions?.map((q, i) => (
                <div key={i} className="bg-gray-50 rounded-lg p-4">
                  <p className="font-medium text-gray-900">{q.question}</p>
                  {q.tip && (
                    <p className="text-sm text-gray-600 mt-2">{q.tip}</p>
                  )}
                </div>
              ))}
            </div>
          </Section>
        )}

        {/* Talking Points */}
        <Section
          id="talking"
          icon={LightBulbIcon}
          title="Your Talking Points"
          count={prepData.talkingPoints?.length}
        >
          <ul className="space-y-2">
            {prepData.talkingPoints?.map((point, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-green-50 rounded-lg">
                <span className="text-green-500 font-bold">✓</span>
                <span className="text-gray-700">{point}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Questions to Ask */}
        <Section
          id="questions"
          icon={QuestionMarkCircleIcon}
          title="Questions to Ask Them"
          count={prepData.questionsToAsk?.length}
        >
          <ul className="space-y-2">
            {prepData.questionsToAsk?.map((q, i) => (
              <li key={i} className="flex items-start gap-3 p-3 bg-blue-50 rounded-lg">
                <span className="text-blue-500">→</span>
                <span className="text-gray-700">{q}</span>
              </li>
            ))}
          </ul>
        </Section>

        {/* Preparation Tips */}
        {prepData.preparationTips?.length > 0 && (
          <Section
            id="tips"
            icon={AcademicCapIcon}
            title="Preparation Tips"
            count={prepData.preparationTips?.length}
          >
            <ul className="space-y-2">
              {prepData.preparationTips?.map((tip, i) => (
                <li key={i} className="flex items-start gap-3 text-gray-700">
                  <span className="text-indigo-500 font-bold">{i + 1}.</span>
                  {tip}
                </li>
              ))}
            </ul>
          </Section>
        )}
      </div>
    </div>
  );
};

InterviewPrepCard.propTypes = {
  jobId: PropTypes.string.isRequired,
  resumeId: PropTypes.string,
  jobTitle: PropTypes.string,
  company: PropTypes.string
};

export default InterviewPrepCard;
